// Sitewide dashboard announcement API methods — mixin applied to the
// ApiService prototype chain. Methods run with `this` bound to the
// ApiService instance, so this.request() resolves through the shared
// HttpClient base.
const withAnnouncementsApi = (Base) => class extends Base {
  /** Any signed-in user: the active announcement (if any) plus whether they've dismissed it. */
  async getActiveAnnouncement() {
    return this.request('/announcements/active');
  }

  /** A student's upcoming canceled classes, for their own courses and electives. */
  async getClassNotices() {
    return this.request('/announcements/class-notices');
  }

  /** Admin-only: the raw current state (even if inactive), for the management panel. */
  async getAdminAnnouncement() {
    return this.request('/announcements/admin');
  }

  /**
   * Admin-only: post a new announcement, or update the live one.
   * options: expectedVersion (the revision the admin loaded; the server answers 409 if it moved on),
   * sendEmail + emailSubject + emailMode ('all' | 'new'), quietEdit (keep the version so students who
   * dismissed it are not shown it again), expiresAt (ISO time to stop showing it, or null).
   */
  async postAnnouncement(message, audience = { type: 'all' }, options = {}) {
    const { expectedVersion, sendEmail, emailSubject, emailMode, quietEdit, expiresAt, coversClasses } = options;
    return this.request('/announcements', {
      method: 'POST',
      body: JSON.stringify({
        message,
        audience,
        expectedVersion,
        sendEmail,
        emailSubject: sendEmail ? emailSubject : undefined,
        emailMode: sendEmail ? emailMode : undefined,
        quietEdit: quietEdit || undefined,
        expiresAt: expiresAt || null,
        coversClasses: coversClasses && coversClasses.length > 0 ? coversClasses : undefined,
      }),
    });
  }

  /** Admin-only: email the live announcement to students who have not been emailed it yet. */
  async emailRemainingAnnouncement(emailSubject) {
    return this.request('/announcements/email-remaining', {
      method: 'POST',
      body: JSON.stringify({ emailSubject }),
    });
  }

  /** Admin-only: send the announcement email to the signed-in admin only, as a proof. */
  async sendAnnouncementTestEmail(message, emailSubject) {
    return this.request('/announcements/admin/test-email', {
      method: 'POST',
      body: JSON.stringify({ message, emailSubject }),
    });
  }

  /** Admin-only: past announcements, newest first. */
  async getAnnouncementHistory() {
    return this.request('/announcements/admin/history');
  }

  /** Admin-only: one past announcement with its audience's names, to load back into the editor. */
  async getAnnouncementHistoryEntry(id) {
    return this.request(`/announcements/admin/history/${id}`);
  }

  /** Admin-only: courses and electives an announcement can be aimed at ({ courses, electives }). */
  async getAnnouncementCourses() {
    return this.request('/announcements/admin/courses');
  }

  /** Admin-only: how many students an audience reaches, and which targets were dropped as inactive. */
  async countAnnouncementAudience(audience) {
    return this.request('/announcements/admin/audience-count', {
      method: 'POST',
      body: JSON.stringify({ audience }),
    });
  }

  /** Admin-only: turn pasted emails into students ({ students, notFound }). */
  async resolveAnnouncementStudents(emails) {
    return this.request('/announcements/admin/resolve-students', {
      method: 'POST',
      body: JSON.stringify({ emails }),
    });
  }

  /** Admin-only: manually take down the active announcement. */
  async deactivateAnnouncement() {
    return this.request('/announcements/deactivate', { method: 'POST' });
  }

  /** Dismiss the current version for the signed-in user only. */
  async dismissAnnouncement(version) {
    return this.request('/announcements/dismiss', {
      method: 'POST',
      body: JSON.stringify({ version }),
    });
  }
};

export default withAnnouncementsApi;
